/**
 * Maker-recommended supplies (owner, 2026-09-30: "We can actually look on manufacturer websites for best cleaning
 * supplies for different models"; "look on Clive"). When a place's equipment names a brand and model family, the supply
 * that equipment's guide asks for is the product the MAKER names for it, shown first as "Recommended by <maker>", with
 * one Amazon SEARCH for that exact product name. The catalog's generic default (products.ts) stays the fallback for any
 * brand or model we have no source for ("Other / not sure").
 *
 * The facts are in makersupplies-data.ts; every one cites a row of docs/source/research/maker-supplies.md (MS1, MS2, ...).
 *
 * Rules (test/makersupplies.test.ts, each with a control):
 * | M1 Cited | every pick and every warning cites rows that exist in maker-supplies.md, each with a URL, a section, a quote
 *   of 15 words or fewer and a lastChecked date |
 * | M2 No ASIN without a source | a pick carries a search query only. The product's name in the query is the name its
 *   source row gives (`searchName`); no ASIN is ever carried (products.ts P5: an ASIN needs a date checked on Amazon's own page) |
 * | M3 Maker first | a retailer-only pick (Clive Coffee) is labelled "Recommended by Clive Coffee", never as the maker's, and the
 *   maker's own pick for the same purpose always ranks ahead of it |
 * | M4 Fallback | an unknown brand, an unknown model or "Other / not sure" gives no pick: productFor returns the generic default |
 * | M5 Warnings | a maker's "do not use" line is shown as a stop line on the guide steps it applies to, and names the maker |
 * | M6 Truthful | no price, stock or rating; the label says "Recommended by <who named it>", and a search says "Find ... on Amazon" |
 * | M7 Levels (phase 2, surfaces) | three levels, most specific first: the model family, the brand ("Recommended by GE Appliances"), then what it is
 *   made of ("For glass-ceramic cooktop surfaces"); a brand's own text overrides the material default only where it names a product or a
 *   warning; no level, the generic default. The level is always said |
 * | M8 Sanitizing stays first | a surface's "do not use" never replaces sanitizing: on a sanitizer step it carries "Sanitizing is still
 *   required (Food Code 4-501.114)" and no unsourced alternative (research/surface-supplies.md) |
 * | M9 Fuzzy | a typed brand is matched to the nearest known one with the finder's matcher (makerfind.ts); no match asks one question, "What is it
 *   made of?" |
 */
import type { ProductPurposeId } from "./products.ts";
import { MAKER_BRANDS, MAKER_MATERIALS, MAKER_PICKS, MAKER_WARNINGS, type MakerBrand, type MakerFamily, type MakerMaterial, type MakerPick, type MakerWarning } from "./makersupplies-data.ts";

export type { MakerBrand, MakerFamily, MakerMaterial, MakerPick, MakerWarning } from "./makersupplies-data.ts";
export { MAKER_BRANDS, MAKER_MATERIALS, MAKER_PICKS, MAKER_WARNINGS } from "./makersupplies-data.ts";

/**
 * What an equipment record says about its make: a brand id, optionally a model family id, and/or what it is made of (a material id:
 * "What is it made of?" when the brand or model isn't listed). Absent = not sure. Brand wins over material; material wins over
 * the generic default.
 */
export interface MakerRef { brand?: string; family?: string; material?: string }

/** The word the picker shows for "none of these". Choosing it clears the record's brand and model: the generic default applies. */
export const MAKER_OTHER = "Other / not sure";

export const brandById = (id: string | undefined | null): MakerBrand | undefined => (id ? MAKER_BRANDS.find((b) => b.id === id) : undefined);
export const familyById = (brand: string | undefined | null, family: string | undefined | null): MakerFamily | undefined =>
  brandById(brand)?.families.find((f) => f.id === family);

export const materialById = (id: string | undefined | null): MakerMaterial | undefined => (id ? MAKER_MATERIALS.find((m) => m.id === id) : undefined);

/** Brands with something researched for an equipment type (the picker's first list). */
export function brandsForType(typeId: string): MakerBrand[] {
  return MAKER_BRANDS.filter((b) => b.typeIds?.includes(typeId) || b.families.some((f) => f.typeIds.includes(typeId)));
}
/** Materials with something researched that this equipment type can be made of ("What is it made of?"). */
export const materialsForType = (typeId: string): MakerMaterial[] => MAKER_MATERIALS.filter((m) => m.typeIds.includes(typeId));
/** Model families of a brand for an equipment type (the picker's second list). */
export function familiesFor(brand: string, typeId: string): MakerFamily[] {
  return brandById(brand)?.families.filter((f) => f.typeIds.includes(typeId)) ?? [];
}
/** Does anything researched exist for this equipment type? (The picker shows only then.) */
export const hasMakerData = (typeId: string): boolean => brandsForType(typeId).length > 0 || materialsForType(typeId).length > 0;

/** A ref only when its ids are real (an unknown or stale id behaves as "not sure"). */
export function cleanRef(ref: MakerRef | null | undefined): MakerRef | undefined {
  if (!ref) return undefined;
  const b = brandById(ref.brand);
  const f = b?.families.find((x) => x.id === ref.family);
  const m = materialById(ref.material);
  if (!b && !m) return undefined;
  return { ...(b ? { brand: b.id } : {}), ...(f ? { family: f.id } : {}), ...(m ? { material: m.id } : {}) };
}

/** The id a pick or warning names for a material. */
export const materialKey = (id: string): string => `material:${id}`;

const matches = (applies: readonly string[], ref: MakerRef): boolean =>
  applies.some((a) => (!!ref.brand && a === ref.brand) || (!!ref.family && a === ref.family) || (!!ref.material && a === materialKey(ref.material)));

/** The maker's own pages first, a retailer's second (M3); then a pick for this model family before one for the whole brand. */
export const specificity = (applies: readonly string[], ref: MakerRef): number =>
  ref.family && applies.includes(ref.family) ? 0 : ref.brand && applies.includes(ref.brand) ? 1 : 2;
export const rankPicks = <T extends { by: "maker" | "retailer"; applies: string[] }>(list: T[], ref: MakerRef): T[] =>
  list.slice().sort((a, b) => (a.by === b.by ? specificity(a.applies, ref) - specificity(b.applies, ref) : a.by === "maker" ? -1 : 1));

/**
 * The picks for a purpose on this make, the maker's first (M3). A pick that names a model family applies to that family
 * only; one that names a brand applies to the whole brand. No ref, or no match: [] (M4).
 */
export function makerPicksFor(purpose: ProductPurposeId, ref: MakerRef | null | undefined): MakerPick[] {
  const r = cleanRef(ref);
  if (!r) return [];
  return rankPicks(MAKER_PICKS.filter((p) => p.purpose === purpose && matches(p.applies, r)), r);
}

/** Which level a pick spoke at for this ref: the model family, the brand, or only the material. */
export function pickLevel(p: Pick<MakerPick, "applies">, ref: MakerRef | null | undefined): "family" | "brand" | "material" {
  const r = cleanRef(ref);
  const s = r ? specificity(p.applies, r) : 1;
  return s === 0 ? "family" : s === 1 ? "brand" : "material";
}

/**
 * "Recommended by La Marzocco", "Recommended by Clive Coffee": who named it, never more (M3). When the pick is only for what the
 * thing is made of: "For glass-ceramic cooktop surfaces" (the person never said a brand), so the level is always said (S1).
 */
export function recommendedLine(p: Pick<MakerPick, "byName"> & Partial<Pick<MakerPick, "applies">>, ref?: MakerRef | null): string {
  if (p.applies && pickLevel({ applies: p.applies }, ref) === "material") {
    const m = materialById(cleanRef(ref)?.material);
    if (m) return `For ${m.name.toLowerCase()} surfaces`;
  }
  return `Recommended by ${p.byName}`;
}

/** The picks any purpose has on this make, for the equipment's own page. */
export function makerPicksAll(ref: MakerRef | null | undefined): MakerPick[] {
  const r = cleanRef(ref);
  if (!r) return [];
  return rankPicks(MAKER_PICKS.filter((p) => matches(p.applies, r)), r);
}

/** The "do not use" lines for an equipment guide step (M5): by guide op and the step's words. */
export function makerStopsFor(ref: MakerRef | null | undefined, opId: string, stepText: string): MakerWarning[] {
  const r = cleanRef(ref);
  if (!r) return [];
  return MAKER_WARNINGS.filter((w) => matches(w.applies, r) && w.opIds.includes(opId) && w.stepWords.test(stepText));
}

/** The stop line as the step shows it: the rule, then who said it, then (on a sanitizing step) what still applies. */
export const stopLine = (w: Pick<MakerWarning, "say" | "byName"> & { food?: string }): string => `${w.say} (${w.byName})${w.food ? ` ${w.food}` : ""}`;

/**
 * The brand and model a person picked, checked (the API and both mock APIs use this one function). `null` or "" for the
 * brand is "Other / not sure": both are cleared and the generic default applies (M4). A model needs its brand.
 */
export function parseMakerPatch(b: { makerBrand?: unknown; makerFamily?: unknown }): { ok: true; set: { makerBrand?: string; makerFamily?: string } | "clear" } | { ok: false; message: string } {
  const brand = b.makerBrand;
  if (brand === null || brand === "") return { ok: true, set: "clear" };
  if (typeof brand !== "string") return { ok: false, message: "Pick a brand from the list, or Other / not sure." };
  const bd = brandById(brand);
  if (!bd) return { ok: false, message: "That brand isn't in the list. Pick one, or Other / not sure." };
  const fam = b.makerFamily;
  if (fam === undefined || fam === null || fam === "") return { ok: true, set: { makerBrand: bd.id } };
  if (typeof fam !== "string" || !bd.families.some((f) => f.id === fam)) return { ok: false, message: `That model isn't in the ${bd.name} list. Pick one, or Other / not sure.` };
  return { ok: true, set: { makerBrand: bd.id, makerFamily: fam } };
}

/** One line on a guide step, drawn under it: a maker's "do not use" (stop) or "your maker names a different product" (note). */
export interface StepMakerLine { kind: "stop" | "note"; say: string; by: string; row: string }

/** The products the shared guides name by brand ("Add up to ½ teaspoon of Cafiza"), and the purpose each stands for. */
const NAMED_IN_STEPS: { word: RegExp; purpose: ProductPurposeId }[] = [
  { word: /\bCafiza\b/i, purpose: "espresso_cleaner" }, { word: /\bRinza\b/i, purpose: "wand_cleaner" }, { word: /\bGrindz\b/i, purpose: "grinder_tablets" },
];

/**
 * Everything the maker adds to a guide step: its stop lines (M5) and, where the shared guide names a product by brand and the
 * maker names a different one for this model, one note saying so (the amount comes from the maker's manual, never the other brand's).
 */
export function makerLinesFor(ref: MakerRef | null | undefined, opId: string, stepText: string): StepMakerLine[] {
  const r = cleanRef(ref);
  if (!r) return [];
  const out: StepMakerLine[] = makerStopsFor(r, opId, stepText).map((w) => ({ kind: "stop", say: stopLine(w), by: w.byName, row: w.rows[0]! }));
  for (const n of NAMED_IN_STEPS) {
    const m = n.word.exec(stepText);
    const pick = m ? makerPicksFor(n.purpose, r)[0] : undefined;
    if (m && pick && !n.word.test(pick.name)) {
      out.push({ kind: "note", say: `${pick.byName} names ${pick.name} for this model. Use the amount in its manual.`, by: pick.byName, row: pick.rows[0]! });
    }
  }
  return out;
}

/** "What is it made of?": a material id, or null / "" for not sure. Separate from the brand: saving one never clears the other. */
export function parseMaterialPatch(v: unknown): { ok: true; material: string | null } | { ok: false; message: string } {
  if (v === null || v === "") return { ok: true, material: null };
  if (typeof v !== "string" || !materialById(v)) return { ok: false, message: "Pick what it is made of from the list, or Not sure." };
  return { ok: true, material: v };
}
